import { FC } from 'react'
import classNames from 'classnames'
import { Button } from '@/atoms/Button'
import { FlexColumn, FlexRow } from '@/atoms/FlexContainers'
import { CheckIcon } from '@/atoms/Icons/CheckIcon'
import { Image } from '@/atoms/Image'
import { AsH2, CaptionMD, Text, TitleMD, TitleSM } from '@/atoms/Text'
import { useTheaterPreferences } from '@/organisms/TheaterPreferences'
import { formatE164ToUSFormat } from '@/utils/phone-utils'
import { useTranslate } from '@/utils/translate/translate-client'

interface Props {
  onClose: () => void
}

const CloseButton: FC<{ onClose: () => void }> = ({ onClose }) => (
  <button
    onClick={onClose}
    className="absolute right-4 top-4 rounded-full bg-core-gray-300 p-2 transition-colors duration-200 hover:bg-core-gray-400"
    aria-label="Close"
  >
    <svg
      className="h-4 w-4 text-core-gray-900"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  </button>
)

export const AutobookConfirmationStep: FC<Props> = ({ onClose }) => {
  const { t } = useTranslate('account')
  const { phoneNumber, preferencesData } = useTheaterPreferences()
  const { optInStatus } = preferencesData?.theatricalUserPreferences || {}
  const userConfirmedPhone = optInStatus === 'SUBSCRIBED'

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden p-6 text-start md:p-12">
      <CloseButton onClose={onClose} />

      <div className="flex min-h-0 flex-1 flex-col items-center overflow-y-auto pb-4">
        <Image
          className="mb-6 w-full max-w-[135px] md:mb-8 md:max-w-[150px]"
          src={
            userConfirmedPhone
              ? 'v1742574227/angel-web/seat-confirmation-illustration'
              : 'v1754679256/angel-web/phone-verification-image'
          }
          alt={t('saveMySeatConfirmation', 'Save My Seats confirmation illustration')}
          width={280}
          height={173}
        />

        <Text
          as={AsH2}
          className={classNames('mb-4 text-start md:mb-6', 'md:photon-heading-sm', 'photon-title-lg text-center')}
          weight="bold"
        >
          {userConfirmedPhone
            ? t('youreAllSetSaveMySeatEnrollmentConfirmed', "You're All Set! 'Save My Seats' Enrollment Confirmed")
            : t('almostTherePleaseVerifyYourPhoneNumber', 'Almost There! Please Verify Your Phone Number.')}
        </Text>

        <Text
          className={classNames('mb-6 leading-[26px] md:mb-8', 'photon-body-sm md:photon-body-lg')}
          color="core-gray-700"
        >
          {userConfirmedPhone
            ? t(
                'youveSuccessfullyEnrolledInSaveMySeat',
                "You've successfully enrolled in Save My Seats. You'll be the first to get text notifications about showtimes at your favorite theaters!",
              )
            : t(
                'justOneMoreStepVerifyYourNumberSoWeCanTextYouWhenYourSeatsAreReady',
                'Just one step left! Verify your number so we can text you when your seats are ready -- and make it easy to confirm, change, or cancel your reservation.',
              )}
        </Text>

        <FlexColumn className="w-full gap-4 rounded-2xl bg-core-gray-100 p-4">
          <TitleMD weight="semibold">
            {!userConfirmedPhone && t('makeSureYouveCompletedAllSteps', "Make sure you've completed all steps:")}
            {userConfirmedPhone && t('youveCompletedAllSteps', "You've completed all the steps!")}
          </TitleMD>
          <FlexColumn className="gap-2">
            <FlexRow className="!items-start justify-start gap-2">
              <CheckIcon color={userConfirmedPhone ? 'green' : 'core-gray-500'} size={24} />
              <FlexColumn className="gap-1">
                <TitleSM weight="semibold">{t('verifyYourPhoneNumber', 'Verify your phone number')}</TitleSM>

                {!userConfirmedPhone && phoneNumber && (
                  <CaptionMD weight="normal" color="core-gray-700">
                    {t('sentToPhoneNumber', `Sent to {{phoneNumber}}`, {
                      phoneNumber: formatE164ToUSFormat(phoneNumber),
                    })}
                  </CaptionMD>
                )}
                {!phoneNumber && (
                  <CaptionMD weight="normal" color="warning-700">
                    {t(
                      'thereWasAnErrorWithYourPhoneNumber',
                      'There was an error with your phone number, please contact support@angel.com',
                    )}
                  </CaptionMD>
                )}
              </FlexColumn>
            </FlexRow>

            <FlexRow className="gap-2">
              <CheckIcon color="green" size={24} />
              <TitleSM weight="semibold">{t('selectPreferredTheater', 'Select Preferred Theater')}</TitleSM>
            </FlexRow>
            <FlexRow className="gap-2">
              <CheckIcon color="green" size={24} />
              <TitleSM weight="semibold">{t('selectPreferredDaysAndTimes', 'Select preferred days and times')}</TitleSM>
            </FlexRow>
          </FlexColumn>
        </FlexColumn>
      </div>

      <div className="w-full shrink-0 pt-4">
        <Button onClick={onClose} variant="black" className="w-full">
          {t('close', 'Close')}
        </Button>
      </div>
    </div>
  )
}
